import React from 'react'
import { useSpring, animated } from '@react-spring/web';
import { Box, Center, Flex, Link, useTheme } from '@chakra-ui/react'
import styles from '../styles/global.module.css'
import Resume from '../assets/resume/Ravin-Io-Oct142026-Resume.pdf'
import ContactButtons from '../components/contactBtns'

interface HomeProps {
  activeTheme: string;
  onThemeSwitch: (theme: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  imgGradient: string;
}

const Home: React.FC<HomeProps> = ({ activeTheme, onThemeSwitch, onMouseEnter, onMouseLeave, imgGradient }) => {
  const theme = useTheme();

  const switchTheme = () => {
    const themeIndex = theme.indexOf(activeTheme);
    const nextThemeIndex = (themeIndex + 1) % theme.length;
    const nextTheme = theme[nextThemeIndex];
    onThemeSwitch(nextTheme);
  };

  const headingStyle = {
    color: theme.styles[activeTheme].color,
    fontFamily: theme.styles[activeTheme].heading,
  };

  const bodyStyle = {
    color: theme.styles[activeTheme].color,
    fontFamily: theme.styles[activeTheme].body,
  };

  const textAnimation = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 500,
    config: {
      mass: 5,
      friction: 150,
      tension: 150,
    },
  });

  const boxAnimation = useSpring({
    from: { opacity: 0, transform: 'translateX(-50px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 1000,
    config: {
      mass: 5,
      friction: 150,
      tension: 150,
    },
  });

  return (
    <Center 
      h='100vh'
      position='relative'
      p={{ base: '0px 16px 16px', md: '0px 24px 24px'}}    
      id='home'
    >
      <Flex 
        alignItems='center' 
        flexDirection={{ base: 'column', sm: 'row'}} 
        justifyContent='center' 
        height='100vh'
        maxWidth='992px'
      >
        <animated.div style={boxAnimation}>
        <Box 
          className={styles.myImage}
          mt={{ base: '20', sm: '0' }}
          width={{ base: '90vw', sm: '35vw', lg: '400px' }}
          height={{ base: '40vh', sm: '60vh', md: '80vh' }}
          style={{ '--dynamic-theme-gradient': imgGradient } as React.CSSProperties}
        ></Box>
        </animated.div>
        <Flex
          height={{ base: '60vh', md: '80vh'}}
          alignItems={{ base: 'flex-start', sm: 'flex-end'}}
          pl='2'
          style={headingStyle}
        >
          <animated.div style={textAnimation}>
          <Box pb='24px'>
            <h1>ravin io</h1>
            <h2>my designs are mysterious and important</h2>
          </Box>
          <Box pb='24px'>
            <p style={bodyStyle}>
              If you caught the <i>Severance</i> reference above, we’re already off to a great start. 
              On this side of the elevator, I’m a Lead UX Designer who thrives at the intersection of complex technology and human-centered design. 
              Moving beyond my roots in front-end development I now lead design teams focusing on empathy-driven and scalable design systems, 
              accessible architecture, and turning high-level "what-ifs" into functional reality. 
              All this by capitalizing on AI-powered research workflows to accelerate output and justify design decisions at lightning speed.
              <br />
              For more info, check out my resume{' '}
                <Link
                  textDecor='underline' 
                  href={Resume}
                  isExternal
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  target='_blank'
                >
                  here
                </Link>
              .
            </p>
          </Box>
          
          <ContactButtons 
            activeTheme={activeTheme} 
            onThemeSwitch={switchTheme}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave} 
          />
          </animated.div>
        </Flex>
        
      </Flex>
    </Center>
  );
};

export default Home;
